import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, ChevronRight, Star, ExternalLink, MessageSquare, Sparkles } from 'lucide-react';
import { GoogleReview, GoogleReviewsResponse } from '../types';
import useIsMobile from '../hooks/useIsMobile';

// Google "G" logo as inline SVG for authenticity badge
function GoogleGIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

// Build the Google "Write a Review" URL from a Place ID
function getGoogleReviewUrl(placeId: string): string {
  if (!placeId) {
    // Fallback: link to the Google Business Profile search page
    return 'https://www.google.com/search?q=MBJ+-+Made+by+Jahangir';
  }
  return `https://search.google.com/local/writereview?placeid=${placeId}`;
}

export default function Testimonials() {
  const isMobile = useIsMobile();
  const [reviews, setReviews] = useState<GoogleReview[]>([]);
  const [overallRating, setOverallRating] = useState<number>(0);
  const [totalReviews, setTotalReviews] = useState<number>(0);
  const [placeId, setPlaceId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // Fetch reviews from our server proxy
  useEffect(() => {
    let cancelled = false;

    async function fetchReviews() {
      try {
        const res = await fetch('/api/reviews');
        if (!res.ok) throw new Error('Network error');
        const data: GoogleReviewsResponse = await res.json();

        if (!cancelled) {
          if (data.configured && data.rating > 0) {
            setReviews(data.reviews || []);
            setOverallRating(data.rating);
            setTotalReviews(data.totalReviews);
            setPlaceId(data.placeId || 'ChIJhZ2DVakDGTkR3DsrYshWG00');
          } else {
            // Fallback to current live ratings of "MBJ - Made by Jahangir" if API is unconfigured
            setReviews([]);
            setOverallRating(5);
            setTotalReviews(2);
            setPlaceId('ChIJhZ2DVakDGTkR3DsrYshWG00');
          }
        }
      } catch (err) {
        console.error('Failed to fetch reviews, using fallback values:', err);
        if (!cancelled) {
          setReviews([]);
          setOverallRating(5);
          setTotalReviews(2);
          setPlaceId('ChIJhZ2DVakDGTkR3DsrYshWG00');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchReviews();
    return () => { cancelled = true; };
  }, []);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? reviews.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === reviews.length - 1 ? 0 : prev + 1));
  };

  // Indexes for 3-card layout
  const prevIndex = reviews.length > 0 ? (currentIndex - 1 + reviews.length) % reviews.length : 0;
  const nextIndex = reviews.length > 0 ? (currentIndex + 1) % reviews.length : 0;

  const googleReviewUrl = getGoogleReviewUrl(placeId);
  const hasReviews = reviews.length > 0;
  const hasRatingOnly = !hasReviews && totalReviews > 0 && overallRating > 0;

  return (
    <section 
      id="testimonials" 
      className="py-16 scroll-mt-24 relative overflow-hidden bg-transparent text-neutral-900 dark:text-white border-y border-neutral-200/20 dark:border-neutral-900/40 transition-colors duration-500"
    >
      {/* Background Ambience */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-950/20 via-neutral-950/20 to-neutral-950 pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -top-40 right-20 w-[450px] h-[450px] bg-emerald-500/5 blur-[130px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -bottom-40 left-10 w-[550px] h-[550px] bg-emerald-500/5 blur-[150px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Title Block */}
        <motion.div 
          initial={isMobile ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 50, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: false, amount: 0.2 }}
          transition={isMobile ? { duration: 0.2 } : { duration: 0.6, ease: "easeOut" }}
          className="flex flex-col items-center text-center max-w-4xl mx-auto mb-10 space-y-3"
        >
          <span className="font-mono text-[10px] tracking-[0.4em] text-emerald-500 font-extrabold uppercase flex items-center gap-2">
            <GoogleGIcon className="w-3.5 h-3.5" />
            Google Reviews
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-sans tracking-tight text-white font-medium hover:text-emerald-500 transition-colors duration-300 cursor-default">
            <span className="font-serif italic font-semibold text-neutral-300 dark:text-neutral-200 mr-3 hover:text-emerald-400 transition-colors duration-300">
              Collaboration,
            </span>
            in their words.
          </h2>

          {/* Overall rating summary when reviews exist */}
          {hasReviews && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="flex items-center gap-3 mt-2"
            >
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-4 h-4 ${i < Math.round(overallRating) ? 'fill-emerald-500 text-emerald-500' : 'text-neutral-700'}`} 
                  />
                ))}
              </div>
              <span className="font-mono text-xs text-neutral-400">
                {overallRating.toFixed(1)} · {totalReviews} review{totalReviews !== 1 ? 's' : ''} on Google
              </span>
            </motion.div>
          )}
        </motion.div>

        {/* Loading State */}
        {loading ? (
          <div className="max-w-md mx-auto text-center py-16">
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        ) : hasReviews ? (
          <>
            {/* Review Carousel */}
            <motion.div 
              initial={isMobile ? { opacity: 1, y: 0, scale: 1, rotate: 0 } : { opacity: 0, y: 80, scale: 0.92, rotate: 1 }}
              whileInView={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
              viewport={{ once: false, amount: 0.12 }}
              transition={isMobile ? { duration: 0.2 } : { type: "spring", stiffness: 60, damping: 14 }}
              className="relative max-w-5xl mx-auto h-[440px] sm:h-[360px] md:h-[320px] flex items-center justify-center"
            >
              <div className="relative w-full h-full flex items-center justify-center overflow-visible">
                
                {/* Prev Card (Desktop only) */}
                {reviews.length > 1 && (
                  <div 
                    onClick={handlePrev}
                    className="hidden md:flex absolute left-0 w-[24%] lg:w-[28%] h-[80%] opacity-20 hover:opacity-40 blur-[1px] transition-all duration-500 cursor-pointer scale-[0.85] origin-right pointer-events-auto transform -translate-x-[115%] lg:-translate-x-[105%] flex-col justify-between bg-white/40 dark:bg-[#0e0f15]/80 border border-neutral-200/50 dark:border-neutral-800 rounded-2xl p-6 select-none"
                  >
                    <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-300 line-clamp-4 italic">
                      "{reviews[prevIndex].text}"
                    </p>
                    <div className="flex items-center gap-3 pt-4 border-t border-neutral-200/50 dark:border-neutral-900/60 w-full">
                      <div className="overflow-hidden">
                        <h4 className="font-semibold text-xs text-neutral-800 dark:text-neutral-200 truncate">{reviews[prevIndex].authorName}</h4>
                        <p className="font-mono text-[8px] text-neutral-500 truncate uppercase mt-0.5">{reviews[prevIndex].relativeTime}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Main Focus Card */}
                <div className="relative w-full max-w-[440px] h-full z-20 flex flex-col justify-between bg-white/70 dark:bg-[#0b0c11]/90 border border-emerald-500/40 shadow-[0_0_35px_rgba(16,185,129,0.12)] rounded-xl p-6 sm:p-7 transition-all duration-300">
                  
                  {/* Google badge + Stars */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1 text-emerald-500">
                      {[...Array(5)].map((_, i) => {
                        const currentRating = reviews[currentIndex]?.rating || 5;
                        const isFilled = i < currentRating;
                        return (
                          <Star 
                            key={i} 
                            className={`w-3.5 h-3.5 ${isFilled ? 'fill-emerald-500 text-emerald-500' : 'text-neutral-300 dark:text-neutral-800'}`} 
                          />
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-neutral-100/80 dark:bg-neutral-900/60 border border-neutral-200/50 dark:border-neutral-800/60">
                      <GoogleGIcon className="w-3 h-3" />
                      <span className="font-mono text-[8px] tracking-wider text-neutral-500 dark:text-neutral-400 uppercase font-bold">Google</span>
                    </div>
                  </div>

                  {/* Review text */}
                  <p className="text-xs sm:text-sm text-neutral-800 dark:text-neutral-100 font-sans leading-relaxed flex-grow">
                    "{reviews[currentIndex]?.text || 'No review text.'}"
                  </p>

                  {/* Profile info row */}
                  <div className="flex items-center justify-between gap-4 pt-4 mt-3 border-t border-neutral-200/50 dark:border-neutral-900/60">
                    <div className="flex items-center gap-3">
                      {/* Reviewer avatar */}
                      {reviews[currentIndex]?.authorPhoto ? (
                        <img 
                          src={reviews[currentIndex].authorPhoto} 
                          alt={reviews[currentIndex].authorName}
                          className="w-9 h-9 rounded-full border-2 border-emerald-500/30 object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full border-2 border-emerald-500/30 bg-emerald-500/10 flex items-center justify-center">
                          <span className="text-emerald-500 font-bold text-sm">
                            {reviews[currentIndex]?.authorName?.charAt(0) || 'G'}
                          </span>
                        </div>
                      )}
                      <div>
                        <h4 className="font-sans text-sm sm:text-base font-semibold tracking-tight text-neutral-900 dark:text-white mb-0.5">
                          {reviews[currentIndex]?.authorName || 'Google User'}
                        </h4>
                        <p className="font-mono text-[9px] sm:text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-widest">
                          {reviews[currentIndex]?.relativeTime || ''}
                        </p>
                      </div>
                    </div>

                    {/* Decorative quote */}
                    <div className="text-4xl sm:text-5xl font-serif text-emerald-500/70 select-none animate-pulse">
                      ""
                    </div>
                  </div>
                </div>

                {/* Next Card (Desktop only) */}
                {reviews.length > 1 && (
                  <div 
                    onClick={handleNext}
                    className="hidden md:flex absolute right-0 w-[24%] lg:w-[28%] h-[80%] opacity-20 hover:opacity-40 blur-[1px] transition-all duration-500 cursor-pointer scale-[0.85] origin-left pointer-events-auto transform translate-x-[115%] lg:translate-x-[105%] flex-col justify-between bg-white/40 dark:bg-[#0e0f15]/80 border border-neutral-200/50 dark:border-neutral-800 rounded-2xl p-6 select-none"
                  >
                    <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-300 line-clamp-4 italic">
                      "{reviews[nextIndex].text}"
                    </p>
                    <div className="flex items-center gap-3 pt-4 border-t border-neutral-200/50 dark:border-neutral-900/60 w-full">
                      <div className="overflow-hidden">
                        <h4 className="font-semibold text-xs text-neutral-800 dark:text-neutral-200 truncate">{reviews[nextIndex].authorName}</h4>
                        <p className="font-mono text-[8px] text-neutral-500 truncate uppercase mt-0.5">{reviews[nextIndex].relativeTime}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                {reviews.length > 1 && (
                  <>
                    <button
                      onClick={handlePrev}
                      className="absolute left-4 md:left-[10%] lg:left-[12%] z-30 p-3 sm:p-4 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg hover:shadow-emerald-500/20 active:scale-90 transition-all cursor-pointer transform -translate-x-1/2"
                      title="Previous Review"
                      data-cursor="hover"
                    >
                      <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 stroke-[2.5]" />
                    </button>
                    <button
                      onClick={handleNext}
                      className="absolute right-4 md:right-[10%] lg:right-[12%] z-30 p-3 sm:p-4 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg hover:shadow-emerald-500/20 active:scale-90 transition-all cursor-pointer transform translate-x-1/2"
                      title="Next Review"
                      data-cursor="hover"
                    >
                      <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 stroke-[2.5]" />
                    </button>
                  </>
                )}
              </div>
            </motion.div>

            {/* Carousel pips */}
            {reviews.length > 1 && (
              <div className="flex justify-center items-center gap-1.5 mt-8 sm:mt-12 md:mt-16 mb-10 relative z-10">
                {reviews.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      currentIndex === i ? 'w-6 bg-emerald-500' : 'w-1.5 bg-neutral-800'
                    }`}
                  />
                ))}
              </div>
            )}
          </>
        ) : hasRatingOnly ? (
          /* ─── Rated State — Reviews exist but details not yet available via API ─── */
          <motion.div
            initial={isMobile ? { opacity: 1 } : { opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="max-w-lg mx-auto text-center py-12 sm:py-16 mb-6 relative"
          >
            {/* Floating sparkle decorations */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <motion.div
                animate={{ y: [-8, 8, -8], opacity: [0.3, 0.7, 0.3] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-4 left-[15%]"
              >
                <Sparkles className="w-4 h-4 text-emerald-500/40" />
              </motion.div>
              <motion.div
                animate={{ y: [4, -8, 4], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                className="absolute bottom-8 right-[15%]"
              >
                <Star className="w-3 h-3 text-emerald-500/20 fill-emerald-500/20" />
              </motion.div>
            </div>

            {/* Big star rating display */}
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-6 relative"
            >
              <span className="text-3xl font-bold text-emerald-500">{overallRating.toFixed(1)}</span>
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                <Star className="w-2.5 h-2.5 text-white fill-white" />
              </div>
            </motion.div>

            {/* Star row */}
            <div className="flex items-center justify-center gap-1.5 mb-4">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.1 * i + 0.2 }}
                >
                  <Star className={`w-6 h-6 ${i < Math.round(overallRating) ? 'fill-emerald-500 text-emerald-500' : 'text-neutral-700'}`} />
                </motion.div>
              ))}
            </div>

            {/* Google badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-neutral-100/80 dark:bg-neutral-900/60 border border-neutral-200/50 dark:border-neutral-800/60 mb-5">
              <GoogleGIcon className="w-3.5 h-3.5" />
              <span className="font-mono text-[9px] tracking-wider text-neutral-500 dark:text-neutral-400 uppercase font-bold">
                {totalReviews} Review{totalReviews !== 1 ? 's' : ''} on Google
              </span>
            </div>

            <h3 className="font-sans text-xl sm:text-2xl font-semibold text-neutral-800 dark:text-white mb-3">
              Rated {overallRating.toFixed(1)} out of 5
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed max-w-sm mx-auto mb-8">
              Our clients have spoken — see our reviews on Google or add yours to the collection.
            </p>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <motion.a
                href={googleReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl font-mono text-[10px] sm:text-xs tracking-widest uppercase font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-500/15 hover:shadow-emerald-500/30 transition-all duration-300 cursor-pointer no-underline"
                data-cursor="hover"
              >
                <GoogleGIcon className="w-4 h-4" />
                Leave a Review
                <ExternalLink className="w-3.5 h-3.5 opacity-60" />
              </motion.a>

              <a
                href={`https://www.google.com/maps/place/?q=place_id:${placeId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-mono text-[10px] sm:text-xs tracking-widest uppercase font-bold text-neutral-400 hover:text-white border border-neutral-800 hover:border-emerald-500/40 transition-all duration-300 cursor-pointer no-underline"
                data-cursor="hover"
              >
                View on Google Maps
                <ExternalLink className="w-3 h-3 opacity-50" />
              </a>
            </div>
          </motion.div>
        ) : (
          /* ─── Empty State — No Reviews Yet ─────────────────────────── */
          <motion.div
            initial={isMobile ? { opacity: 1 } : { opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="max-w-lg mx-auto text-center py-12 sm:py-16 mb-6 relative"
          >
            {/* Floating sparkle decorations */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <motion.div
                animate={{ y: [-8, 8, -8], opacity: [0.3, 0.7, 0.3] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-4 left-[15%]"
              >
                <Sparkles className="w-4 h-4 text-emerald-500/40" />
              </motion.div>
              <motion.div
                animate={{ y: [6, -6, 6], opacity: [0.2, 0.6, 0.2] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute top-8 right-[20%]"
              >
                <Star className="w-3.5 h-3.5 text-emerald-500/30 fill-emerald-500/30" />
              </motion.div>
              <motion.div
                animate={{ y: [-5, 10, -5], opacity: [0.4, 0.8, 0.4] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                className="absolute bottom-12 left-[25%]"
              >
                <Sparkles className="w-3 h-3 text-emerald-500/30" />
              </motion.div>
              <motion.div
                animate={{ y: [4, -8, 4], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                className="absolute bottom-8 right-[15%]"
              >
                <Star className="w-3 h-3 text-emerald-500/20 fill-emerald-500/20" />
              </motion.div>
            </div>

            {/* Google icon in container */}
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-6 relative"
            >
              <GoogleGIcon className="w-9 h-9" />
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                <MessageSquare className="w-2.5 h-2.5 text-white" />
              </div>
            </motion.div>

            <h3 className="font-sans text-xl sm:text-2xl font-semibold text-neutral-800 dark:text-white mb-3">
              Be the first to review
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed max-w-sm mx-auto mb-8">
              We just launched — your feedback on Google helps shape our story and inspires future collaborators.
            </p>

            {/* Empty state star display */}
            <div className="flex items-center justify-center gap-1.5 mb-8">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.1 * i + 0.3 }}
                >
                  <Star className="w-6 h-6 text-neutral-300 dark:text-neutral-700" />
                </motion.div>
              ))}
            </div>

            {/* Primary CTA — opens Google review in new tab */}
            <motion.a
              href={googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl font-mono text-[10px] sm:text-xs tracking-widest uppercase font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-500/15 hover:shadow-emerald-500/30 transition-all duration-300 cursor-pointer"
              data-cursor="hover"
            >
              <GoogleGIcon className="w-4 h-4" />
              Leave a Review on Google
              <ExternalLink className="w-3.5 h-3.5 opacity-60" />
            </motion.a>
          </motion.div>
        )}

        {/* ─── Persistent CTA Strip (visible when reviews exist) ─────────── */}
        {hasReviews && !loading && (
          <div className="max-w-md mx-auto mt-6 bg-neutral-100/60 dark:bg-neutral-950/20 border border-neutral-200 dark:border-neutral-900 rounded-lg p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
              <h3 className="font-sans text-sm font-semibold text-neutral-800 dark:text-white flex items-center gap-2">
                <GoogleGIcon className="w-3.5 h-3.5 shrink-0" />
                Share your experience
              </h3>
              <a
                href={googleReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-4 py-2 rounded-md font-mono text-[9px] tracking-wider uppercase font-bold text-neutral-800 dark:text-white bg-neutral-200/50 dark:bg-emerald-600/15 border border-neutral-300 dark:border-emerald-500/40 hover:bg-neutral-300 dark:hover:bg-emerald-600 dark:hover:border-emerald-500 transition-all active:scale-95 duration-200 no-underline"
                data-cursor="hover"
              >
                <Star className="w-3 h-3" />
                Review on Google
                <ExternalLink className="w-3 h-3 opacity-50" />
              </a>
            </div>
          </div>
        )}

      </div>
    </section>
  );
}
